import { Button, Table } from "@fronterahealth/frontera-ui-components";

import { Maybe, ProviderMetadataType } from "@api/graphql/types-and-hooks";
import { parseJson } from "@components/FieldComponent/utils";
import { ReportFieldDate } from "@components/ReportField/inputs/ReportFieldDate";
import { ReportFieldInput } from "@components/ReportField/inputs/ReportFieldInput";
import { ReportFieldInputBaseProps } from "@components/ReportField/types";
import { useAdminData } from "@providers/AdminDataProvider";

export interface AssessmentAppointmentDataRow {
  id: number;
  index: number;
  date: string;
  times: string;
  location: string;
  assessmentMethods: string;
  evaluatorsPresent: string;
}

const getDefaultRow = (id: number, providerMetadata?: Maybe<ProviderMetadataType>): AssessmentAppointmentDataRow => {
  const evaluatorText = providerMetadata
    ? [
        [providerMetadata.firstName || "", providerMetadata.lastName || ""].join(" ").trim(),
        providerMetadata.professionalTitle,
        providerMetadata.licenseNumber,
      ]
        .filter(Boolean)
        .join(", ")
        .trim()
    : "";

  return {
    id,
    index: id - 1,
    date: "",
    times: "",
    location: "Telehealth:",
    assessmentMethods: "",
    evaluatorsPresent: evaluatorText,
  };
};

const getDefaultRows = (providerMetadata?: Maybe<ProviderMetadataType>): AssessmentAppointmentDataRow[] => [
  { ...getDefaultRow(1, providerMetadata), assessmentMethods: "Parent Interview & Observation" },
  { ...getDefaultRow(2, providerMetadata), assessmentMethods: "VBMAPP & Observation" },
  {
    ...getDefaultRow(3, providerMetadata),
    assessmentMethods: "Assessment Review & Goal Alignment; Reviewed Intervention",
  },
];

export const ReportFieldAssessmentAppointments: React.FC<ReportFieldInputBaseProps> = ({
  value,
  onChange,
  disabled,
  variant,
}) => {
  const { loginProviderDetailsQuery } = useAdminData();
  const providerMetadata = loginProviderDetailsQuery.data?.getLoginProvider
    ?.providermetadata as Maybe<ProviderMetadataType>;

  const savedRows = parseJson<AssessmentAppointmentDataRow[]>(value);
  const rows = savedRows && savedRows.length > 0 ? savedRows : getDefaultRows(providerMetadata);
  const isDisabled = !!disabled;

  const saveRows = (updatedRows: AssessmentAppointmentDataRow[]) => onChange(JSON.stringify(updatedRows));

  const updateRow = (rowId: number, field: keyof AssessmentAppointmentDataRow, fieldValue: string) =>
    saveRows(rows.map((row) => (row.id === rowId ? { ...row, [field]: fieldValue } : row)));

  const handleAddRow = () => {
    const newId = Math.max(...rows.map((row) => row.id), 0) + 1;
    saveRows([...rows, getDefaultRow(newId, providerMetadata)]);
  };

  const handleDeleteRow = (rowId: number) => {
    saveRows(rows.filter((row) => row.id !== rowId));
  };

  return (
    <div className="space-y-4 relative">
      <div className="overflow-visible">
        <Table
          defaultItemsPerPage={20}
          cellClassName="cursor-default overflow-visible"
          data={rows}
          columns={[
            {
              dataIndex: "date",
              key: "date",
              title: "Date",
              className: "border-r align-top w-1/7",
              render: (_, record) => (
                <ReportFieldDate
                  id={`${record.id}-date`}
                  placeholder="Select date"
                  disabled={isDisabled}
                  value={record.date}
                  variant={variant}
                  onChange={(value) => updateRow(record.id, "date", value)}
                  className="text-sm"
                />
              ),
            },
            {
              dataIndex: "times",
              key: "times",
              title: "Times",
              className: "border-r align-top w-1/7",
              render: (_, record) => (
                <ReportFieldInput
                  id={`${record.id}-times`}
                  placeholder="Enter times"
                  value={record.times}
                  disabled={isDisabled}
                  variant={variant}
                  onChange={(value) => updateRow(record.id, "times", value)}
                  className="text-sm"
                />
              ),
            },
            {
              dataIndex: "location",
              key: "location",
              title: "Location",
              className: "border-r align-top w-1/7",
              render: (_, record) => (
                <ReportFieldInput
                  id={`${record.id}-location`}
                  placeholder="Enter location"
                  value={record.location}
                  disabled={isDisabled}
                  variant={variant}
                  onChange={(value) => updateRow(record.id, "location", value)}
                  className="text-sm"
                />
              ),
            },
            {
              dataIndex: "assessmentMethods",
              key: "assessmentMethods",
              title: "Assessment methods / tools used",
              className: "border-r align-top w-2/7",
              render: (_, record) => (
                <ReportFieldInput
                  id={`${record.id}-assessmentMethods`}
                  placeholder="Enter location"
                  value={record.assessmentMethods}
                  disabled={isDisabled}
                  variant={variant}
                  onChange={(value) => updateRow(record.id, "assessmentMethods", value)}
                  className="text-sm"
                />
              ),
            },
            {
              dataIndex: "evaluatorsPresent",
              key: "evaluatorsPresent",
              title: "Evaluator(s) present",
              className: "align-top w-2/7",
              render: (_, record) => (
                <ReportFieldInput
                  id={`${record.id}-evaluatorsPresent`}
                  placeholder="Enter evaluator(s)"
                  value={record.evaluatorsPresent}
                  disabled={isDisabled}
                  variant={variant}
                  onChange={(value) => updateRow(record.id, "evaluatorsPresent", value)}
                  className="text-sm"
                />
              ),
            },
          ]}
        />
      </div>
      {!isDisabled && (
        <div className="flex gap-2">
          <Button appearance="secondary" text="Add Row" onClick={handleAddRow} />
          {rows.length > 1 && (
            <Button
              appearance="link"
              text="Delete Last Row"
              onClick={() => handleDeleteRow(rows[rows.length - 1].id)}
              className="text-red-600 hover:text-red-700"
            />
          )}
        </div>
      )}
    </div>
  );
};
